export interface Rect {
    x: number;
    y: number;
    width: number;
    height: number;
}

export interface ManagedPanel extends Rect {
    id: string;
}

/**
 * A simple window manager to handle panel placement, ensuring panels
 * stay within screen bounds and don't overlap.
 */
export class PanelManager {
    private panels: ManagedPanel[] = [];
    private screenWidth: number;
    private screenHeight: number;
    private static readonly CASCADE_OFFSET = 30; // pixels

    constructor(screenWidth: number, screenHeight: number) {
        this.screenWidth = screenWidth;
        this.screenHeight = screenHeight;
    }

    public updateScreenSize(width: number, height: number): void {
        this.screenWidth = width;
        this.screenHeight = height;
        // Potentially add logic here to reposition existing panels if they go out of bounds
    }

    public addPanel(id: string, requestedRect: Rect): ManagedPanel {
        let { x, y, width, height } = requestedRect;

        // First, ensure the requested position is initially within bounds
        x = Math.max(0, Math.min(x, this.screenWidth - width));
        y = Math.max(0, Math.min(y, this.screenHeight - height));

        let attempts = 0;
        const maxAttempts = 50; // Increased attempts for more complex layouts

        // Loop until we find a non-overlapping position
        while (this.isOverlapping({ id, x, y, width, height }) && attempts < maxAttempts) {
            // Cascade down and to the right
            x += PanelManager.CASCADE_OFFSET;
            y += PanelManager.CASCADE_OFFSET;

            // If we go off-screen during cascade, reset to top-left and continue the loop
            if (x + width > this.screenWidth || y + height > this.screenHeight) {
                x = PanelManager.CASCADE_OFFSET;
                y = PanelManager.CASCADE_OFFSET;
            }
            attempts++;
        }

        const newPanel: ManagedPanel = { id, x, y, width, height };
        this.panels.push(newPanel);
        return newPanel;
    }

    public removePanel(id: string): void {
        this.panels = this.panels.filter(p => p.id !== id);
    }
    
    public bringToFront(id: string): void {
        const panel = this.panels.find(p => p.id === id);
        if (panel) {
            this.removePanel(id);
            this.panels.push(panel);
        }
    }

    private isOverlapping(panel: ManagedPanel): boolean {
        for (const existing of this.panels) {
            if (existing.id === panel.id) continue;

            const noOverlap =
                panel.x >= existing.x + existing.width ||
                panel.x + panel.width <= existing.x ||
                panel.y >= existing.y + existing.height ||
                panel.y + panel.height <= existing.y;
            
            if (!noOverlap) {
                return true; // Found an overlap
            }
        }
        return false;
    }
} 